import { all, call, put, fork, takeEvery, select } from 'redux-saga/effects';
import {
    fetchEmpsSuccess,
    showEmpMessage,
    onSaveEmpSuccess,
    fetchEmpSurveyAnswersSuccess,
    hideEmpLoader,
    fetchNotificationsSuccess,
    addFavouriteEmpSuccess,
} from 'actions/Employee';
import { fetchOrgSurveyAnswersSuccess, onSaveOrgSurveyAnswers, hideOrgSurveyAnswersLoader, showOrgSurveyAnswersMessage } from 'actions/OrgSurveyAnswers';
import { fetchSurveysSuccess } from 'actions/Survey';
import { fetchSurveyGroupsSuccess } from 'actions/SurveyGroup';
import { fetchSurveyQuestionsSuccess } from 'actions/SurveyQuestion';
import { ON_SAVE_EMP, FETCH_ALL_EMP, FETCH_EMP_SURVEY_ANSWERS, SAVE_EMP_SURVEY_ANSWERS, FETCH_NOTIFICATIONS, ADD_FAVOURITE_EMP } from 'constants/ActionTypes';
import { update, post } from 'services/ApiClient';
import { endpoint } from 'services/ApiConfig';
import { extractTokenId, getIdentityOrgId, getIdentityId, isAdmin, generateGuid } from 'services/Authentication';
import { getSurveyById, buildEmpSurveyAnswers, getEmpSurveyAnswers } from 'services/SurveyService';
import { fetchDataRequest, updateDataRequest } from 'services/ApiService';
import { objectStateComparator, auditLogHeaderEntry } from 'services/auditService';
import { isEqual } from 'lodash';
import { DATA_NOT_CHANGED_SO_NOT_UPDATED } from 'constants/messages';
import { ORGID_PATH_PARAM, ORG_SURVEY_ANSWERS_ENDPOINT, EMPLOYEE_FETCH_ENDPOINT, EMPLOYEE_UPDATE_ENDPOINT, EMPLOYEE_ADD_ENDPOINT } from 'constants/api';

const orgStatisticsData = (state) => state.orgs.orgStatisticsData;
const currentOrg = (state) => state.orgs.selectedOrg;

// TODO add defaults if null passed for time and icon
const createNotification = (image, title, time, icon) => {
    return { image, title, time, icon };
};

const fetchNotificationsRequest = async (identity, orgStatistics) => {
    const notifications = [];
    // const statistics = await fetchDataRequest('organisationNotifications', ['orgid'], [selectedOrgId]);
    notifications.push(createNotification('assets/images/notification-150_150.jpg', identity.name, '1:20 PM', 'zmdi-view-day text-grey'));
    if (isAdmin()) {
        notifications.push(
            createNotification(
                'assets/images/notification-150_150.jpg',
                'Number of current projects = '.concat(orgStatistics?.projectCount || 0),
                '1:20 PM',
                'zmdi-comment-text text-grey'
            )
        );
        if (orgStatistics?.projectsWithSameDate === 0) {
            notifications.push(
                createNotification(
                    'assets/images/notification-150_150.jpg',
                    'Number of imported projects that have not been updated = '.concat(orgStatistics?.projectsWithSameDate),
                    '1:20 PM',
                    'zmdi-calendar text-grey'
                )
            );
        }
        if (orgStatistics?.projectsWithNoPriorities === 0) {
            notifications.push(
                createNotification(
                    'assets/images/notification-150_150.jpg',
                    'Number of projects without priorities = '.concat(orgStatistics?.projectsWithNoPriorities),
                    '1:20 PM',
                    'zmdi-blur-off text-grey'
                )
            );
        }
    }
    return notifications;
};

export function* fetchNotifications({ payload }) {
    try {
        const orgStatistics = yield select(orgStatisticsData);
        // payload = identity = { id, orgid, role, name }
        // pass org statistics data
        // pass selected organisation
        const fetchedNotifications = yield call(fetchNotificationsRequest, payload, orgStatistics) || {};
        yield put(fetchNotificationsSuccess(fetchedNotifications));
    } catch (error) {
        yield put(showEmpMessage(error.messages));
    }
}

const fetchEmployeesRequest = async (selectedOrgId) => {
    const employees = await fetchDataRequest(EMPLOYEE_FETCH_ENDPOINT, [ORGID_PATH_PARAM], [selectedOrgId]);
    return employees;
};

function* fetchEmployees() {
    try {
        const selectedOrg = yield select(currentOrg);
        const fetchedEmployees = yield call(fetchEmployeesRequest, selectedOrg.id) || [];
        yield put(fetchEmpsSuccess(fetchedEmployees));
    } catch (error) {
        yield put(showEmpMessage(error.messages));
    }
}

const getEmpList = (state) => state.emps.empList;

function* saveEmployee({ payload }) {
    try {
        const selectedOrg = yield select(currentOrg);
        const allEmps = yield select(getEmpList);
        const savedEmployees = yield call(saveEmployeeRequest, allEmps, selectedOrg, payload);
        yield put(onSaveEmpSuccess(savedEmployees));
    } catch (error) {
        yield put(showEmpMessage(error.messages));
    }
}

const saveEmployeeRequest = async (allEmps, selectedOrg, payload) => {
    try {
        let isNew = true;
        let currentEmpState = {};
        let updatedPayload = { ...payload };
        for (const emp of allEmps) {
            if (emp.id === payload.id) {
                isNew = false;
                break;
            }
        }

        const endpointConfig = isNew ? endpoint(EMPLOYEE_ADD_ENDPOINT) : endpoint(EMPLOYEE_UPDATE_ENDPOINT);
        if (isNew) {
            // eslint-disable-next-line no-unused-vars
            const newPayload = { ...payload, password: generateGuid(), registerorg: false, orgid: selectedOrg.id }; // will be using passwordless so password wont do anything
            if (endpointConfig) {
                await post(endpointConfig.uri, newPayload)
                    .then((response) => {
                        // the response will return a token with the id so place it back into the local object
                        const id = response ? extractTokenId(response.token) : 'addEmpResponseError';
                        updatedPayload = { ...newPayload, id: id };
                        // empList.push(updatedPayload);
                    })
                    .catch((error) => {
                        throw error;
                    });
            }
        } else {
            if (endpointConfig) {
                if (!isEqual(currentEmpState, payload)) {
                    const changedState = objectStateComparator([currentEmpState], [payload]);
                    const uri = endpointConfig.uri.replace('{orgid}', selectedOrg.id);
                    await update(uri, payload, auditLogHeaderEntry(changedState))
                        .then((payload) => payload) // already in empList
                        .catch((error) => {
                            throw error;
                        });
                } else {
                    throw DATA_NOT_CHANGED_SO_NOT_UPDATED;
                }
            }
        }
        return updatedPayload;
    } catch (error) {
        throw error;
    }
};

const fetchSurveys = async () => {
    return await fetchDataRequest('surveys');
};

const fetchSurveyGroups = async () => {
    return await fetchDataRequest('surveyGroups');
};

const fetchSurveyQuestions = async () => {
    return await fetchDataRequest('surveyQuestions');
};

/**
 * called when the survey starts - NOTE the survey will exist in the org
 * if the emp answers do not exist then they will be created (on first time accessed for an org)
 * then update the selected org survey answers for a future update
 * return the questionAnswers object
 * @param {*} param0 - action.payload = { orgSurveyId, surveyId }
 */
function* fetchEmpSurveyAnswers({ payload }) {
    // payload = surveyId
    const orgId = getIdentityOrgId();
    const empId = getIdentityId();
    const { orgSurveyId, surveyId } = payload;

    try {
        // need to ensure survey details have been stored to continue
        // survey details are static so simply retrieve once and re use
        // TODO build array and run these calls in parrallel using yield all[]
        const surveys = yield call(fetchSurveys);
        yield put(fetchSurveysSuccess(surveys));
        const surveyGroups = yield call(fetchSurveyGroups);
        yield put(fetchSurveyGroupsSuccess(surveyGroups));
        const surveyQuestions = yield call(fetchSurveyQuestions);
        yield put(fetchSurveyQuestionsSuccess(surveyQuestions));
        // }
        // need to reload the survey every time as it may be a different selection
        const answers = yield call(fetchDataRequest, ORG_SURVEY_ANSWERS_ENDPOINT, [ORGID_PATH_PARAM, 'orgSurveyId'], [orgId, orgSurveyId]) || {};
        const orgSurveyAnswers = answers?.length > 0 ? answers[0] : answers;
        if (Object.keys(orgSurveyAnswers).length > 0) {
            // need to extract the employees answers from the org survey answers and add to the store
            let empSurveyAnswers = {};
            if (orgSurveyAnswers?.questionAnswers) {
                // check if the employee question answers exist
                const filteredEmpSurveyAnswers = yield call(getEmpSurveyAnswers, orgSurveyAnswers, empId);
                if (filteredEmpSurveyAnswers?.length === 0) {
                    // do not have any survey answers stored so create new object
                    const survey = yield call(getSurveyById, surveys, surveyId);
                    let builtEmpSurveyAnswers = {};
                    if (survey) {
                        builtEmpSurveyAnswers = yield call(buildEmpSurveyAnswers, survey, surveyGroups);
                    }
                    empSurveyAnswers = { empId: empId, answers: builtEmpSurveyAnswers };
                    // add to the local object and then update the org survey answers data table
                    orgSurveyAnswers.questionAnswers.push(empSurveyAnswers);
                    const updatedOrgSurveyAnswers = yield call(saveEmpSurveyAnswerRequest, orgSurveyAnswers, empSurveyAnswers);
                    yield put(onSaveOrgSurveyAnswers(updatedOrgSurveyAnswers));
                } else {
                    empSurveyAnswers = filteredEmpSurveyAnswers[0];
                }
            }
            yield put(fetchOrgSurveyAnswersSuccess(orgSurveyAnswers));
            yield put(fetchEmpSurveyAnswersSuccess(empSurveyAnswers));
        } else {
            // hide all loaders that are invoked and show a message as survey answers should exist
            yield put(showOrgSurveyAnswersMessage('Org Survey Answers were not initialised correctly!'));
            yield put(showEmpMessage(Array.fill('Org Survey Answers were not initialised correctly!')));
            yield put(hideOrgSurveyAnswersLoader());
            yield put(hideEmpLoader());
        }
    } catch (error) {
        yield put(hideOrgSurveyAnswersLoader());
        yield put(hideEmpLoader());
        yield put(showOrgSurveyAnswersMessage(error.messages));
        yield put(showEmpMessage(error.messages));
    }
}

const getOrgSurveyAnswers = (state) => state.orgSurveyAnswers.orgSurveyAnswers;

/**
 * Simply sets up the payload to pass onto saveOrgSurveyAnswers
 * @param {payload} param0 
 * {
    "empId": "634d1475334f91eeedf1e03b20190525",
    "answers": [
      {
        "questionId": "mission_and_purpose_v1",
        "answer": 4
      },
      ...
    ]
  }
 */
function* saveEmpSurveyAnswers({ payload }) {
    try {
        // overwrite the current entry or add if new to the existing org survey list - will update orgSurveyAnswers
        const currentOrgSurveyAnswers = yield select(getOrgSurveyAnswers);
        const updatedOrgSurveyAnswers = yield call(saveEmpSurveyAnswerRequest, currentOrgSurveyAnswers, payload);
        yield put(onSaveOrgSurveyAnswers(updatedOrgSurveyAnswers));
    } catch (error) {
        yield put(showEmpMessage(error.messages));
    }
}

/**
 * 
 * @param {*} currentOrgSurveyAnswers 
 * orgSurveyAnswers
[
    {
        "id": "883r1475334f91eeedf1e03b20190999",
        "orgid": "634d1475334f91eeedf1e03b20190524",
        "surveyId": "dc00cc7175004a68df46359b20190618",
        "questionAnswers": [
            {
                "empId": "634d1475334f91eeedf1e03b20190525",
                "answers": [
                    {"questionId": "mission_and_purpose_v1", "answer": 4},
                    {"questionId": "agile_planning_v1", "answer": 7}
                ]
            }
        ]
    }
]
 * @param {*} payload orgSurveyAnswers.questionAnswers[entry] - entry to replace
 */
const saveEmpSurveyAnswerRequest = async (currentOrgSurveyAnswers, payload) => {
    try {
        const orgSurveyAnswersList = currentOrgSurveyAnswers.questionAnswers.map((questionAnswer) => {
            if (questionAnswer.empId === payload.empId) {
                return payload;
            } else {
                return questionAnswer;
            }
        });
        return { ...currentOrgSurveyAnswers, questionAnswers: orgSurveyAnswersList };
    } catch (error) {
        throw error;
    }
};

/**
 * This is when the user selects the star on the list page - update the starred attribute of the entity
 * @param {*} param0
 */
function* addFavouriteEmployee({ payload }) {
    try {
        const allEmps = yield select(getEmpList);
        const selectedOrg = yield select(currentOrg);
        const filtered = allEmps.filter((emp) => emp?.id === payload?.id);

        if (filtered?.length > 0) {
            const emp = filtered[0];
            const updatedEmp = {
                ...emp,
                starred: !payload.starred,
            };
            const auditMsg = 'Updating starred setting to '.concat(updatedEmp?.starred).concat(' for employee ').concat(updatedEmp?.id);
            yield call(updateDataRequest, updatedEmp, EMPLOYEE_UPDATE_ENDPOINT, auditMsg, [ORGID_PATH_PARAM], [selectedOrg.id]);
            // now just pass on the original payload as the reducer will filter the list as per the state of the selection
            yield put(addFavouriteEmpSuccess(payload));
        }
    } catch (error) {
        yield put(showEmpMessage(error.messages));
    }
}

// TODO delete employees

export function* fetchEmployeesSaga() {
    yield takeEvery(FETCH_ALL_EMP, fetchEmployees);
}

export function* saveEmployeeSaga() {
    yield takeEvery(ON_SAVE_EMP, saveEmployee);
}

export function* fetchEmpSurveyAnswersSaga() {
    yield takeEvery(FETCH_EMP_SURVEY_ANSWERS, fetchEmpSurveyAnswers);
}

export function* saveEmpSurveyAnswersSaga() {
    yield takeEvery(SAVE_EMP_SURVEY_ANSWERS, saveEmpSurveyAnswers);
}

export function* fetchEmpNotificationsSaga() {
    yield takeEvery(FETCH_NOTIFICATIONS, fetchNotifications);
}

export function* addFavouriteEmployeeSaga() {
    yield takeEvery(ADD_FAVOURITE_EMP, addFavouriteEmployee);
}

export default function* rootSaga() {
    yield all([
        fork(fetchEmpNotificationsSaga),
        fork(addFavouriteEmployeeSaga),
        fork(fetchEmployeesSaga),
        fork(saveEmployeeSaga),
        fork(fetchEmpSurveyAnswersSaga),
        fork(saveEmpSurveyAnswersSaga),
    ]);
}



// WEBPACK FOOTER //
// ./sagas/Employee.js